import React, { useState, useEffect, useContext } from "react";
import { useTheme } from "@material-ui/core/styles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  TextField,
  Grid,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  Switch,
  FormControlLabel,
  List,
  ListItem, 
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Tooltip,
  Paper
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import UndoIcon from "@material-ui/icons/Undo";
import GetAppIcon from "@material-ui/icons/GetApp";
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import { toast } from "react-toastify";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";


const AdjustPriceModal = ({ open, onClose, onSubmit, appointment }) => {
    const isAlreadyCourtesy = parseFloat(appointment?.totalAmount) === 0;

    const [newAmount, setNewAmount] = useState(isAlreadyCourtesy ? 0 : (appointment?.totalAmount || ""));
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);
    
    const [isCourtesy, setIsCourtesy] = useState(isAlreadyCourtesy); 
    const { user } = useContext(AuthContext);

    const handleCourtesyChange = (e) => {
        const checked = e.target.checked;
        setIsCourtesy(checked);
        
        if (checked) {
            setNewAmount(0);
            if (!reason) setReason("Cortesía / Sin Costo");
        } else {
            setNewAmount(appointment?.originalAmount || ""); 
            if (reason === "Cortesía / Sin Costo") setReason("");
        }
    };

    const handleSubmit = async () => {
        if ((newAmount === "" || newAmount === null || newAmount === undefined) || !reason.trim()) {
            toast.error("El nuevo monto y la razón son obligatorios.");
            return;
        }
        setLoading(true);
        try {
            await api.put(`/appointments/${appointment.id}/adjust-price`, {
                newAmount: parseFloat(newAmount),
                reason,
                assistantId: user.id
            });
            toast.success("Precio ajustado correctamente.");
            onSubmit(true);
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Ajustar Monto Total de la Cita</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" gutterBottom>
                    Monto Original: <strong>${parseFloat(appointment?.originalAmount || appointment?.totalAmount || 0).toFixed(2)}</strong>
                </Typography>
                <Box mb={1}>
                    <FormControlLabel
                        control={
                            <Switch 
                                checked={isCourtesy} 
                                onChange={handleCourtesyChange} 
                                color="primary" 
                            />
                        }
                        label="Marcar como Cortesía (Valor $0)"
                    />
                </Box>
                <TextField
                    label="Nuevo Monto Total"
                    variant="outlined"
                    margin="dense"
                    type="number"
                    fullWidth
                    value={newAmount}
                    onChange={(e) => setNewAmount(e.target.value)}
                    disabled={isCourtesy}
                    InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                />
                <TextField
                    label="Razón del Ajuste"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    multiline
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    helperText="Explique el motivo del cambio."
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)} color="secondary" disabled={loading}>
                    Cancelar
                </Button>
                <Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : "Guardar Ajuste"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const PaymentModal = ({ open, onClose, appointment, mode = 'edit' }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [loadingAdjustments, setLoadingAdjustments] = useState(false);
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [otherPaymentMethod, setOtherPaymentMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [payments, setPayments] = useState([]);
  const [adjustPriceModalOpen, setAdjustPriceModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const currentAppointment = appointment;
  const [paymentToRefund, setPaymentToRefund] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [showAdjustments, setShowAdjustments] = useState(false);
  const isViewMode = mode === 'view';

  const standardMethods = ["cash", "card", "transfer"];
  const paymentMethodTranslations = {
    cash: "Efectivo",
    card: "Tarjeta",
    transfer: "Transferencia",
    other: "Otro",
  };

  useEffect(() => {
    if (open && appointment) {

      const fetchPayments = async () => {
        setLoadingPayments(true);
        try {
          const { data } = await api.get(`/appointments/${appointment.id}/payments`);
          setPayments(data);
        } catch (err) {
          toastError(err);
        } finally {
          setLoadingPayments(false);
        }
      };

      const fetchAdjustments = async () => {
        setLoadingAdjustments(true);
        try {
          const { data } = await api.get(`/appointments/${appointment.id}/adjustments`);
          setAdjustments(data);
        } catch (err) {
          console.error("No se pudo cargar el historial de ajustes", err);
        } finally {
          setLoadingAdjustments(false);
        }
      };

      fetchPayments();
      fetchAdjustments();

      const totalPaid = appointment.totalPaid || 0;
      const remaining = parseFloat(appointment.totalAmount) - totalPaid;
      setAmount(remaining > 0 ? remaining.toFixed(2) : "");

      setPaymentMethod("cash");
      setOtherPaymentMethod("");

    } else {
      setPayments([]);
      setAdjustments([]);
      setShowAdjustments(false);
    }
  }, [open, appointment]);

  const totalPaid = payments.reduce((sum, payment) => {
    const paymentAmount = parseFloat(payment.amount);
    return payment.transactionType === 'refund' ? sum - paymentAmount : sum + paymentAmount;
  }, 0);
  
  const remainingBalance = (currentAppointment?.totalAmount || 0) - totalPaid;

  const handleClose = () => {
    onClose(false);
    setPaymentToRefund(null);
  };

  const handleExportPDF = async () => {
		const input = document.getElementById('payment-content-to-export');
		if(!input) return;

		try {
			setLoading(true);
			const canvas = await html2canvas(input, { scale: 2 }); 
			const imgData = canvas.toDataURL('image/png');

			const imgWidth = canvas.width * 0.75;
      const imgHeight = canvas.height * 0.75;
      const headerHeight = 150; 

			const pdf = new jsPDF({
          orientation: 'p',
          unit: 'pt',
          format: [imgWidth, imgHeight + headerHeight]
      });

			const margin = 20;

			pdf.setFontSize(28);
			pdf.text("Resumen de Movimientos", 40, 60);
			
			pdf.setFontSize(16);
			pdf.setTextColor(100);
			
			const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
			const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
			const formattedDate = new Date().toLocaleDateString(undefined, dateOptions);
			const formattedTime = new Date().toLocaleTimeString(undefined, timeOptions);
			const emissionLine = "Fecha de Emisión: " + formattedDate + " - " + formattedTime + " (Hora Local)";
			pdf.text(emissionLine, 40, 100);

			pdf.addImage(imgData, 'PNG', 0, headerHeight, imgWidth, imgHeight);
			
			const safeName = (currentAppointment.patientName || currentAppointment.contact?.name || "Paciente").replace(/\s/g, '_');
			pdf.save("Comprobante_" + safeName + ".pdf");
			toast.success("PDF descargado correctamente");
		} catch (err) {
			console.error(err);
			toast.error("Error al generar el PDF");
		} finally {
			setLoading(false);
		}
	};

	const handleSendWhatsApp = async () => {
		const historyDiv = document.getElementById('adjustments-history-section');
		if (historyDiv) historyDiv.style.display = 'none';

		const input = document.getElementById('payment-content-to-export');
		if(!input) return;

		setSending(true);
		try {
			const canvas = await html2canvas(input, { scale: 2 }); 
			
			if (historyDiv) historyDiv.style.display = 'block';

			const imgData = canvas.toDataURL('image/png');
			
			const imgWidth = canvas.width * 0.75;
      const imgHeight = canvas.height * 0.75;
      const headerHeight = 150; 

			const pdf = new jsPDF({
				orientation: 'p',
				unit: 'pt',
				format: [imgWidth, imgHeight + headerHeight]
			});

			const margin = 20;

			pdf.setFontSize(28);
			pdf.text("Resumen de Movimientos", 40, 60);
			
			pdf.setFontSize(16);
			pdf.setTextColor(100);
			
			const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
			const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
			const formattedDate = new Date().toLocaleDateString(undefined, dateOptions);
			const formattedTime = new Date().toLocaleTimeString(undefined, timeOptions);
			const emissionLine = "Fecha de Emisión: " + formattedDate + " - " + formattedTime + " (Hora Local)";
			pdf.text(emissionLine, 40, 100);

			pdf.addImage(imgData, 'PNG', 0, headerHeight, imgWidth, imgHeight);

			const pdfBlob = pdf.output('blob');
			const safeName = (currentAppointment.patientName || currentAppointment.contact?.name || "Paciente").replace(/\s/g, '_');
			const fileName = "Resumen_" + safeName + ".pdf";
			const file = new File([pdfBlob], fileName, { type: "application/pdf" });
			
			const formData = new FormData();
			formData.append("file", file);
			formData.append("body", "Le enviamos su estado de cuenta.");

			await api.post("/appointments/" + currentAppointment.id + "/send-summary", formData);

			toast.success("Estado de cuenta enviado por WhatsApp");
		} catch(err) {
			if (historyDiv) historyDiv.style.display = 'block';
			toastError(err);
		} finally {
			setSending(false);
		}
	};

  const handleRegisterPayment = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("El monto debe ser un número positivo.");
      return;
    }

    let finalMethod = paymentMethod;
    if (paymentMethod === "other") {
      if (!otherPaymentMethod.trim()) {
        toast.error("Por favor especifique el método de pago.");
        return;
      }
      finalMethod = otherPaymentMethod.trim();
    }

    setLoading(true);
    try {
      await api.post("/payments", {
        appointmentId: currentAppointment.id,
        amount: parseFloat(amount),
        paymentMethod: finalMethod, 
        notes,
        transactionType: 'payment',
      });
      toast.success("Pago registrado correctamente.");
      onClose(true);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRefund = async () => {
    if (!paymentToRefund) return;

    let finalRefundMethod = paymentMethod;
    if (paymentMethod === "other") {
      if (!otherPaymentMethod.trim()) {
         toast.error("Por favor especifique el método de devolución.");
         return;
      }
      finalRefundMethod = otherPaymentMethod.trim();
    }

    setLoading(true);
    try {
      await api.post("/payments", {
        appointmentId: currentAppointment.id,
        amount: paymentToRefund.amount,
        paymentMethod: finalRefundMethod,
        notes: "Devolución del pago ID: " + paymentToRefund.id,
        transactionType: 'refund',
        refundsPaymentId: paymentToRefund.id,
      });
      toast.success("Devolución registrada correctamente.");
      onClose(true);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
      setConfirmOpen(false);
      setPaymentToRefund(null);
    }
  };

  const handleOpenRefundModal = (payment) => {
    setPaymentToRefund(payment);
    if (standardMethods.includes(payment.paymentMethod)) {
      setPaymentMethod(payment.paymentMethod);
      setOtherPaymentMethod("");
    } else {
      setPaymentMethod("other");
      setOtherPaymentMethod(payment.paymentMethod);
    }
    setConfirmOpen(true);
  };

  if (!currentAppointment) {
    return null;
  }

  const rawDate = currentAppointment.date;
  const rawTime = currentAppointment.startTime || "00:00:00";
  
  const dateObj = new Date(rawDate + "T" + rawTime); 
  
  const formattedDate = dateObj.toLocaleDateString();
  const formattedTime = currentAppointment.startTime ? currentAppointment.startTime.slice(0, 5) : "--:--";
  
  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isViewMode ? "Estado de Cuenta" : "Gestionar Pago"}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <div id="payment-content-to-export" style={{ padding: 8, backgroundColor: theme.palette.background.paper }}>
            
            <Paper variant="outlined" style={{ padding: 12, marginBottom: 16, backgroundColor: theme.palette.background.paper }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">PACIENTE</Typography>
                        <Typography variant="h6">{currentAppointment.patientName || currentAppointment.contact?.name || "Sin nombre"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">DOCTOR</Typography>
                        <Typography variant="body1">
                            {currentAppointment.doctor?.name || "No asignado"}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">FECHA Y HORA</Typography>
                        <Typography variant="body1">
                            {formattedDate} - {formattedTime} hs
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Box mb={2}>
                {parseFloat(currentAppointment.totalAmount) === 0 ? (
                    <Paper variant="outlined" style={{ padding: 16, backgroundColor: theme.palette.warning.light, borderColor: theme.palette.warning.main, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box display="flex" alignItems="center">
                            <Typography variant="h6" style={{fontWeight: 'bold', marginRight: 8 }}>
                                CITA DE CORTESÍA
                            </Typography>
                            <Typography variant="body2" style={{fontWeight: 'bold'}} color="textSecondary">
                                (Sin costo para el paciente)
                            </Typography>
                        </Box>

                        <Button 
                            size="small" 
                            variant="outlined" 
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => setAdjustPriceModalOpen(true)}
                        >
                            Modificar
                        </Button>
                    </Paper>
                ) : (
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <Typography variant="body1">Total:</Typography>
                            <Box display="flex" alignItems="center">
                            <Typography variant="h6">
                                <strong>${parseFloat(currentAppointment.totalAmount).toFixed(2)}</strong>
                            </Typography>
                            <IconButton size="small" onClick={() => setAdjustPriceModalOpen(true)} title="Ajustar Monto Total">
                                <EditIcon fontSize="small" />
                            </IconButton>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="body1" style={{ color: "green" }}>Pagado:</Typography>
                            <Typography variant="h6" style={{ color: "green" }}><strong>${totalPaid.toFixed(2)}</strong></Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="body1" style={{ color: "red" }}>Restante:</Typography>
                            <Typography variant="h6" style={{ color: "red" }}><strong>${remainingBalance.toFixed(2)}</strong></Typography>
                        </Grid>
                    </Grid>
                )}
            </Box>
            <Box mb={2} id="adjustments-history-section">
              {loadingAdjustments ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8 }}>
                    Cargando historial de cambios...
                  </Typography>
                </Box>
              ) : adjustments.length > 0 ? (
                <>
                  <ListItem
                    button
                    onClick={() => setShowAdjustments(!showAdjustments)}
                    style={{ paddingLeft: 0, paddingRight: 0 }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="primary" style={{ fontWeight: 'bold' }}>
                          {showAdjustments
                            ? "Ocultar historial de cambios de precio"
                            : `Ver historial de cambios de precio (${adjustments.length})`}
                        </Typography>
                      }
                    />
                    {showAdjustments ? <ExpandLess color="primary" /> : <ExpandMore color="primary" />}
                  </ListItem>

                  <Collapse in={showAdjustments} timeout="auto" unmountOnExit>
                    <Paper variant="outlined" style={{ backgroundColor: theme.palette.background.default }}>
                        <List dense disablePadding>
                        {adjustments.map((adj, index) => (
                            <React.Fragment key={adj.id}>
                                <ListItem alignItems="flex-start">
                                    <ListItemText
                                        primary={
                                            <Typography variant="caption" style={{ fontWeight: 'bold' }}>
                                                {new Date(adj.adjustmentDate).toLocaleString(undefined, { hour12: false })} - Por: {adj.assistant?.name || 'Sistema'}
                                            </Typography>
                                        }
                                        secondary={
                                            <>
                                                <Typography component="span" variant="body2" color="textPrimary" display="block">
                                                    Cambio: ${parseFloat(adj.previousAmount).toFixed(2)} ➔ <strong>${parseFloat(adj.newAmount).toFixed(2)}</strong>
                                                </Typography>
                                                <Typography component="span" variant="caption" color="textSecondary" display="block" style={{ fontStyle: 'italic', marginTop: 2 }}>
                                                    Motivo: "{adj.reason}"
                                                </Typography>
                                            </>
                                        }
                                    />
                                </ListItem>
                                {index < adjustments.length - 1 && <Divider component="li" />}
                            </React.Fragment>
                        ))}
                        </List>
                    </Paper>
                </Collapse>
                </>
              ) : null}
            </Box>
            
            <Divider style={{ margin: "8px 0" }} />

            <Box mb={2}>
              <Typography variant="subtitle1" gutterBottom>Movimientos</Typography>
              {loadingPayments ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                    <CircularProgress />
                    <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8 }}>
                      Cargando movimientos...
                    </Typography>
                  </Box>
                ) : payments.length > 0 ? (
                <List dense>
                  {payments.map(p => {
                    const translatedMethod = paymentMethodTranslations[p.paymentMethod] || p.paymentMethod;
                    const secondaryText = new Date(p.paymentDate).toLocaleDateString() + " - Por: " + (p.assistant?.name || 'Sistema');
                    const isRefunded = payments.some(refund => refund.refundsPaymentId === p.id);
                    
                    return (
                      <ListItem key={p.id} disableGutters divider>
                        <ListItemText
                          primary={
                            p.transactionType === 'refund'
                              ? "Devolución #" + p.id + " (del Pago #" + p.refundsPaymentId + "): -$" + parseFloat(p.amount).toFixed(2) + " (" + translatedMethod + ")"
                              : "Pago #" + p.id + ": $" + parseFloat(p.amount).toFixed(2) + " (" + translatedMethod + ")"
                          }
                          primaryTypographyProps={{
                            style: { 
                              color: p.transactionType === 'refund' ? '#e65100' : '#2e7d32',
                              fontWeight: 'bold'
                            }
                          }}
                          secondary={secondaryText}
                        />
                        <ListItemSecondaryAction>
                          {p.transactionType === 'payment' && !isRefunded && (
                            <Tooltip title="Registrar Devolución" arrow>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleOpenRefundModal(p)}
                              >
                                <UndoIcon fontSize="small"/>
                              </IconButton>
                            </Tooltip>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary" align="center" style={{padding: 20}}>
                    — Sin transacciones registradas —
                </Typography>
              )}
            </Box>
          </div>
          
          {!isViewMode && (
            <>
              <Divider style={{ margin: "16px 0" }} />
              <Typography variant="h6">Registrar Nuevo Pago</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Monto"
                    variant="outlined"
                    margin="dense"
                    type="number"
                    fullWidth
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Grid container spacing={1}>
                    <Grid item xs={paymentMethod === "other" ? 6 : 12}>
                      <FormControl variant="outlined" margin="dense" fullWidth>
                        <InputLabel>Método</InputLabel>
                        <Select
                          value={paymentMethod}
                          onChange={(e) => {
                             setPaymentMethod(e.target.value);
                             if(e.target.value !== 'other') setOtherPaymentMethod("");
                          }}
                          label="Método"
                        >
                          <MenuItem value="cash">Efectivo</MenuItem>
                          <MenuItem value="card">Tarjeta</MenuItem>
                          <MenuItem value="transfer">Transferencia</MenuItem>
                          <MenuItem value="other">Otro</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {paymentMethod === "other" && (
                      <Grid item xs={6}>
                         <TextField
                            placeholder="Especifique..."
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            value={otherPaymentMethod}
                            onChange={(e) => setOtherPaymentMethod(e.target.value)}
                         />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notas (Opcional)"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {isViewMode && (
            <>
              <Button 
                onClick={handleExportPDF} 
                color="primary" 
                variant="outlined"
                startIcon={<GetAppIcon />}
                disabled={loading}
              >
                {loading ? "Generando..." : "PDF"}
              </Button>
              <Button 
                onClick={handleSendWhatsApp} 
                style={{ backgroundColor: '#25D366', color: theme.palette.background.paper }}
                variant="contained"
                startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <WhatsAppIcon />}
                disabled={loading || sending}
              >
                Enviar PDF al paciente
              </Button>
            </>
          )}
          
          <Button onClick={handleClose} color="secondary" disabled={loading}>
            {isViewMode ? "Cerrar" : "Cancelar"}
          </Button>

          {!isViewMode && (
            <Button color="primary" variant="contained" onClick={handleRegisterPayment} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Registrar Pago"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {paymentToRefund && (
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirmar Devolución</DialogTitle>
          
          <DialogContent dividers>
            <Typography variant="body1" gutterBottom>
              ¿Está seguro de que desea registrar una devolución por el monto de <strong>${parseFloat(paymentToRefund.amount).toFixed(2)}</strong>?
            </Typography>
            
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              Seleccione el método por el cual se realizó el reembolso al paciente:
            </Typography>

            <Box mt={1}>
              <Grid container spacing={1}>
                  <Grid item xs={paymentMethod === "other" ? 6 : 12}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                        <InputLabel>Método de Devolución</InputLabel>
                        <Select
                          value={paymentMethod}
                          onChange={(e) => {
                              setPaymentMethod(e.target.value);
                              if(e.target.value !== 'other') setOtherPaymentMethod("");
                          }}
                          label="Método de Devolución"
                        >
                          <MenuItem value="cash">Efectivo</MenuItem>
                          <MenuItem value="card">Tarjeta</MenuItem>
                          <MenuItem value="transfer">Transferencia</MenuItem>
                          <MenuItem value="other">Otro</MenuItem>
                        </Select>
                    </FormControl>
                  </Grid>
                  {paymentMethod === "other" && (
                    <Grid item xs={6}>
                        <TextField
                            placeholder="Especifique..."
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            value={otherPaymentMethod}
                            onChange={(e) => setOtherPaymentMethod(e.target.value)}
                        />
                    </Grid>
                  )}
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} color="secondary" disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmRefund} color="primary" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Confirmar"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {adjustPriceModalOpen && (
        <AdjustPriceModal 
          open={adjustPriceModalOpen}
          onClose={(shouldRefresh) => {
            setAdjustPriceModalOpen(false);
            if (shouldRefresh) onClose(true);
          }}
          onSubmit={(shouldRefresh) => {
            setAdjustPriceModalOpen(false);
            if (shouldRefresh) onClose(true);
          }}
          appointment={currentAppointment}
        />
      )}
    </>
  );
};

export default PaymentModal;